import { useState, useEffect, useContext } from 'react';
import PropTypes from 'prop-types';
import { useParams } from 'react-router-dom';
import { Button, Input, Loader, ModalGeneric, SelectGeneric } from 'ypf-pas-general-ui-components';
import { Row, Col } from 'react-bootstrap';
import { AiOutlinePlusCircle, AiOutlineMinusCircle } from 'react-icons/ai';

import LoaderContext from '../../../../context/LoaderProvider';
import BoxesWithText from '../../BoxesWithText/BoxesWithText';
import useToast from '../../../../hooks/useToast';
import ErrorMessage from '../../../ErrorMessage/ErrorMessage';
import { postPosLineToUbications } from '../../../../services/monthlyServiceRequest';
import { formatFullDate, formatsEnum } from '../../../../utils/formatUtils';
import useMultiplPositionAndLine from '../../../../hooks/useMultiplePositionAndLine';
import { mapperPosLineArray } from '../../../../utils/mapper';

import DateRange from './DateRange';

const MONTHS = [
  'ENERO',
  'FEBRERO',
  'MARZO',
  'ABRIL',
  'MAYO',
  'JUNIO',
  'JULIO',
  'AGOSTO',
  'SEPTIEMBRE',
  'OCTUBRE',
  'NOVIEMBRE',
  'DICIEMBRE'
];

const getFirstDayOfMonth = (monthParam, yearParam) => {
  const date = new Date(parseInt(yearParam, 10), monthParam, 1);

  return date?.toISOString().split('T')[0];
};

const getLastDateOfMonth = (monthParam, yearParam) => {
  const date = new Date(parseInt(yearParam, 10), monthParam + 1, 0);

  return date?.toISOString().split('T')[0];
};

const formatDateRange = (dateRange) => ({
  fechaInicio: dateRange.startDate,
  fechaFinaliza: dateRange.finalDate || dateRange.startDate
});

const formatUbicationsBody = (dateRanges, ubicationsSelected) => {
  const ubicationIds = ubicationsSelected.map((ubication) => ubication.id);
  const dateRangesByUbication = ubicationIds.map((idUbicacion) => ({
    idUbicacion,
    rangoFechas: []
  }));

  dateRanges.forEach((dateRange) => {
    const index = dateRangesByUbication.findIndex(
      (ubicationDateRange) => ubicationDateRange.idUbicacion === dateRange.ubicationId
    );

    dateRangesByUbication[index].rangoFechas.push(formatDateRange(dateRange));
  });

  return dateRangesByUbication;
};

const AddPLToUbicationModal = ({
  contractNumber,
  ubicationsSelecteds,
  showAddPLToUbicationModal,
  setShowAddPLToUbicationModal,
  month,
  year,
  onSubmit,
  isTemplate,
  withlocation
}) => {
  const firstDayOfSSM = getFirstDayOfMonth(MONTHS.indexOf(month), year);
  const lastDayOfSSM = getLastDateOfMonth(MONTHS.indexOf(month), year);
  const [dateRanges, setDateRanges] = useState([]);
  const [disabledPrimaryAction, setDisabledPrimaryAction] = useState(false);

  const isMonthly = true;
  const {
    positions,
    lines,
    loading,
    pLFieldArrayValues,
    changePLFieldArrayValues,
    duplicatePositionLine,
    handleAddPL,
    handleSuprPL,
    handleChangeLineValue,
    handleChangePositionValue,
    handleChangeAmountValue,
    isPOrLEmpty
  } = useMultiplPositionAndLine({ isMonthly, contractNumber });
  const { id } = useParams();
  const { setNewToast } = useToast();
  const { setLoader } = useContext(LoaderContext);

  const handleChangeStartDate = (value, index) => {
    const updatedDateRanges = dateRanges?.map((dateRange, i) => {
      if (i === index) {
        return {
          ...dateRange,
          startDate: value,
          finalDate: ''
        };
      }

      return dateRange;
    });

    setDateRanges(updatedDateRanges);
  };

  const handleChangeFinalDate = (value, index) => {
    const updatedDateRanges = dateRanges?.map((dateRange, i) => {
      if (i === index) {
        return {
          ...dateRange,
          finalDate: value
        };
      }

      return dateRange;
    });

    setDateRanges(updatedDateRanges);
  };

  const getStartDateOfNewRange = (ubicationId) => {
    const ranges = dateRanges.filter((range) => range.ubicationId === ubicationId);
    const lastRange = ranges[ranges.length - 1];
    const dateToReturn = new Date(lastRange.finalDate || lastRange.startDate);
    const nextDate = new Date(
      dateToReturn.getFullYear(),
      dateToReturn.getUTCMonth(),
      dateToReturn.getUTCDate() + 1
    );

    return formatFullDate(nextDate, formatsEnum.FORMAT_DATE_2);
  };

  const handleAddRange = (ubicationId) => {
    const dateRangeToAdd = {
      ubicationId,
      startDate: getStartDateOfNewRange(ubicationId),
      finalDate: lastDayOfSSM
    };
    const newDateRanges = [...dateRanges, dateRangeToAdd];

    setDateRanges(newDateRanges);
  };

  const handleSuprRange = (ubicationIndex) => {
    const newDateRanges = dateRanges.filter((_, index) => index !== ubicationIndex);

    setDateRanges(newDateRanges);
  };

  const getUbicationDateRanges = (ubicationId) =>
    dateRanges.filter((dateRange) => dateRange.ubicationId === ubicationId);

  const disableAddButton = (ubicationId) => {
    const filteredRanges = getUbicationDateRanges(ubicationId);

    return filteredRanges.some((ubication) => ubication.finalDate === lastDayOfSSM);
  };

  const handleSubmit = async () => {
    setDisabledPrimaryAction(true);
    try {
      setLoader(true);
      const body = {
        posiciones: mapperPosLineArray(pLFieldArrayValues).posiciones,
        lineas: mapperPosLineArray(pLFieldArrayValues).lineas,
        ubicaciones: formatUbicationsBody(dateRanges, ubicationsSelecteds)
      };
      const response = isTemplate ? null : await postPosLineToUbications(id, body);

      setNewToast({
        type: 'SUCCESS',
        message: response?.mensaje
      });
      setShowAddPLToUbicationModal(false);
      onSubmit(pLFieldArrayValues);
      changePLFieldArrayValues([{}]);
    } catch (err) {
      setLoader(false);
      setNewToast({
        type: 'ERROR',
        message: err.message
      });
      setShowAddPLToUbicationModal(false);
      changePLFieldArrayValues([{}]);
    } finally {
      setLoader(false);
    }
    setDisabledPrimaryAction(false);
  };

  useEffect(() => {
    const disabled = !pLFieldArrayValues?.length;

    setDisabledPrimaryAction(disabled);
  }, [pLFieldArrayValues]);
  useEffect(() => {
    const intialDateRanges = ubicationsSelecteds?.map((ubication) => ({
      ubicationId: ubication.id,
      startDate: firstDayOfSSM,
      finalDate: lastDayOfSSM
    }));

    setDateRanges(intialDateRanges);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showAddPLToUbicationModal]);

  return (
    <ModalGeneric
      backdrop="static"
      infoModal
      onHide={() => setShowAddPLToUbicationModal(false)}
      primaryAction={handleSubmit}
      primaryActionDisabled={disabledPrimaryAction || duplicatePositionLine() || isPOrLEmpty}
      primaryActionLabel="Aceptar"
      secondaryAction={() => {
        setShowAddPLToUbicationModal(false);
        changePLFieldArrayValues([{}]);
      }}
      secondaryActionLabel="Salir"
      show={showAddPLToUbicationModal}
      size="xl"
      title="Agregar posición y línea a ubicaciones"
      variantHeader="light"
    >
      <>
        {loading && <Loader show text="CARGANDO ..." />}
        {pLFieldArrayValues?.map((pLFieldArrayValue, index) => (
          <Row key={`${pLFieldArrayValue?.position?.id}-${pLFieldArrayValue?.line?.id}`}>
            <Col sm={4}>
              <SelectGeneric
                defaultValueOneOption={false}
                isDisabled={loading}
                label="Posición"
                name={`position-${index}`}
                onChange={(val) => handleChangePositionValue(val, index)}
                optionMapper={{
                  value: 'id',
                  label: 'nombre'
                }}
                options={positions}
                paddingIndicator={4}
                placeholder="Seleccioná una posición"
                value={pLFieldArrayValue.position}
                withSelect="100%"
              />
            </Col>
            <Col sm={4}>
              <SelectGeneric
                defaultValueOneOption={false}
                isDisabled={!pLFieldArrayValue.position}
                label="Línea"
                name={`line-${index}`}
                onChange={(val) => handleChangeLineValue(val, index)}
                optionMapper={{
                  value: 'id',
                  label: 'nombre'
                }}
                options={lines[index]}
                paddingIndicator={4}
                placeholder="Seleccioná una línea"
                value={pLFieldArrayValue.line}
                withSelect="100%"
              />
            </Col>
            {!withlocation && (
              <Col sm={2} style={{ display: 'flex' }}>
                <Input
                  className="template-name-input w-100"
                  disabled={!pLFieldArrayValue.line}
                  label="Cantidad"
                  onChange={(val) => handleChangeAmountValue(val, index)}
                  placeholder="--"
                  type="number"
                  value={pLFieldArrayValue.amount}
                />
              </Col>
            )}
            <Col sm={2} style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <div
                style={{
                  marginTop: '15px',
                  display: 'flex',
                  gap: '5px',
                  justifyContent: 'center',
                  alignItems: 'center'
                }}
              >
                <Button
                  colorIcon="danger"
                  disabled={pLFieldArrayValues.length === 1}
                  icon={AiOutlineMinusCircle}
                  onClick={() => handleSuprPL(index)}
                  sizeIcon="24px"
                  type="button"
                  variant="light-gray"
                  widthButton="40px"
                />
                <Button
                  colorIcon="primary"
                  disabled={pLFieldArrayValues.find((pLObj) => !pLObj.position || !pLObj.line)}
                  icon={AiOutlinePlusCircle}
                  onClick={() => handleAddPL()}
                  sizeIcon="24px"
                  type="button"
                  variant="light-gray"
                  widthButton="40px"
                />
              </div>
            </Col>
          </Row>
        ))}
        {duplicatePositionLine() && (
          <div>
            <ErrorMessage message="Posición y Línea repetida. Selecciona otra combinación." />
          </div>
        )}
        <hr />
        {ubicationsSelecteds?.length > 0 &&
          ubicationsSelecteds.map((ubication) => (
            <>
              <Row key={ubication.id}>
                <Col sm={7}>
                  <h5>
                    <strong>Ubicación seleccionada</strong>
                  </h5>
                  <div className="d-felx justify-content-between align-items-center">
                    <BoxesWithText>{ubication.nombre}</BoxesWithText>
                  </div>
                </Col>
                <Col sm={5}>
                  <h5>
                    <strong>Realización de la tarea</strong>
                  </h5>
                  {!isTemplate ? (
                    dateRanges.map((dateRange, index) => {
                      if (dateRange.ubicationId === ubication.id) {
                        return (
                          <DateRange
                            disableAddButton={disableAddButton(ubication.id)}
                            disableSuprButton={getUbicationDateRanges(ubication.id).length === 1}
                            finalDate={dateRange.finalDate}
                            handleAddRange={handleAddRange}
                            handleSuprRange={handleSuprRange}
                            id={dateRange.ubicationId}
                            index={index}
                            key={dateRange.startDate.toString()}
                            max={lastDayOfSSM}
                            min={index === 0 ? firstDayOfSSM : dateRange.startDate}
                            onChangeFinalDate={handleChangeFinalDate}
                            onChangeStartDate={handleChangeStartDate}
                            startDate={dateRange.startDate}
                          />
                        );
                      }

                      return null;
                    })
                  ) : (
                    <h5>Todo el mes</h5>
                  )}
                </Col>
              </Row>
              <hr />
            </>
          ))}
      </>
    </ModalGeneric>
  );
};

AddPLToUbicationModal.propTypes = {
  ubicationsSelecteds: PropTypes.arrayOf(
    PropTypes.shape({
      nombre: PropTypes.string,
      id: PropTypes.number
    })
  ).isRequired,
  showAddPLToUbicationModal: PropTypes.bool.isRequired,
  setShowAddPLToUbicationModal: PropTypes.func.isRequired,
  contractNumber: PropTypes.string.isRequired,
  month: PropTypes.string,
  year: PropTypes.string,
  onSubmit: PropTypes.func.isRequired,
  isTemplate: PropTypes.bool,
  withlocation: PropTypes.bool.isRequired
};
AddPLToUbicationModal.defaultProps = {
  month: '',
  year: '',
  isTemplate: false
};
export default AddPLToUbicationModal;
